import { useId, useState, type ReactNode } from 'react';
import { Check, Loader2, X } from '../../../icons';
import type { Icon } from '@phosphor-icons/react';

import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { CONFIRM_WORD, isTypedConfirmMatch } from '../TypedConfirmField/typedConfirm';

/**
 * ActionConfirmDialog — one shared confirm dialog for every approve / reject /
 * remove path across brand + admin. `variant` controls the confirm-button
 * colour and (for reject/remove) whether a required-reason textarea renders.
 * The reason bubbles back through `onConfirm` for reject/remove (approve passes
 * `undefined`) so callers can post it to the activity log.
 */

/** Destructive dialogs whose gate is a plain "are you sure?" (not a
 *  resource-name match) pass this as `confirmText`. */
export { CONFIRM_WORD };

export type ActionConfirmVariant =
  | 'approve'
  | 'approve-as-brand'
  | 'brand-approve'
  | 'admin-approve'
  | 'reject'
  | 'remove'
  // Destructive confirm WITHOUT a reason field — for object deletes/unassigns
  // (e.g. removing a campaign product, unassigning a product from a creator)
  // where an audit reason isn't collected. Same destructive styling as remove.
  | 'delete'
  // Access-management actions — neutral/primary, NOT success. The confirm
  // button is the default (black) primary, since granting/changing access is
  // a plain confirm, not an approval.
  | 'grant'
  | 'change'
  // Restorative action — neutral, not an approval. Same treatment as grant/change.
  | 'reinstate';

export type ActionConfirmCreator = {
  name: string;
  /** Display text — `@username`, not a URL. */
  handle?: string;
  /** When present, `handle` renders as a link that opens this profile
   *  (Instagram/YouTube) in a new tab — same click-through pattern as
   *  PlatformCreatorCell used across the campaign-detail roster tabs. */
  profileUrl?: string;
  isVerified?: boolean;
  avatarUrl?: string;
};

export type ActionConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: ActionConfirmVariant;
  title: string;
  description?: string;
  /** Inline creator card shown above the form. Optional. */
  creator?: ActionConfirmCreator;
  /** Override the default confirm-button label derived from variant. */
  confirmLabel?: string;
  /** Min length applied to the reason field. Defaults to 3. */
  reasonMinLength?: number;
  /** Max length applied to the reason field. Defaults to 2000. */
  reasonMaxLength?: number;
  reasonPlaceholder?: string;
  /** Override the reason field's label. Defaults to "Reason". Shown with a
   *  red asterisk instead of "(optional)" whenever `reasonMinLength > 0`. */
  reasonLabel?: string;
  /**
   * Receives the trimmed reason for reject/remove, undefined for approve. When
   * `confirmText` is set, a second `{ confirm: <typed value> }` argument is
   * passed for the BE's `confirm.Matches` check; callers that don't need it
   * simply ignore it.
   */
  onConfirm: (reason?: string, typedConfirm?: { confirm: string }) => void | Promise<void>;
  /** Parent-owned busy flag (mutation in flight). */
  busy?: boolean;
  /**
   * Caller-supplied form content rendered between the creator card and the
   * reason field (e.g. the approve-as-brand deliverable picker). Keeps the
   * dialog generic; the picked value lives in the caller's state.
   */
  extraContent?: ReactNode;
  /**
   * When false, confirm is disabled even if reasonValid passes — for callers
   * whose `extraContent` has its own required-input gate. Default true.
   */
  canConfirm?: boolean;
  /**
   * Opt-in typed-confirmation gate: when set, renders an input the user must
   * fill with this exact string before confirm arms. Composes with the reason
   * field / `extraContent`. Undefined (default) renders no gate.
   */
  confirmText?: string;
  /**
   * Hide the trailing check/X glyph on the confirm button. Default false
   * (the per-variant glyph is shown). Set true where a caller wants a plain
   * text confirm button — e.g. the admin waitlist disapprove dialog.
   */
  hideConfirmIcon?: boolean;
  /** Focus return on close; Radix only restores focus to a <DialogTrigger>, which this dialog has none of. */
  onCloseAutoFocus?: (event: Event) => void;
};

const DEFAULT_LABELS: Record<ActionConfirmVariant, string> = {
  approve: 'Approve',
  'approve-as-brand': 'Approve as Brand',
  'brand-approve': 'Approve',
  'admin-approve': 'Confirm Approval',
  reject: 'Reject',
  remove: 'Remove creator',
  delete: 'Delete',
  grant: 'Grant access',
  change: 'Change access',
  reinstate: 'Send back to Pending',
};

// Confirm-button colour per variant. brand-approve/admin-approve use the plain
// black `default` (not `success`); reject/remove/delete share destructive red.
const CONFIRM_BTN_VARIANT: Record<ActionConfirmVariant, 'success' | 'destructive' | 'default'> = {
  approve: 'success',
  'approve-as-brand': 'default',
  'brand-approve': 'default',
  'admin-approve': 'default',
  reject: 'destructive',
  remove: 'destructive',
  delete: 'destructive',
  grant: 'default',
  change: 'default',
  reinstate: 'default',
};

// Trailing glyph on the confirm button — Figma shows a check for the
// approve family and an X for the destructive family; grant/change have no
// Figma reference and stay glyph-less (plain confirm, not an approval).
const CONFIRM_BTN_ICON: Partial<Record<ActionConfirmVariant, Icon>> = {
  approve: Check,
  'approve-as-brand': Check,
  'brand-approve': Check,
  'admin-approve': Check,
  reject: X,
  remove: X,
  delete: X,
};

/**
 * The reason textarea renders for reject + remove, but a value is optional —
 * empty just skips the thread message / audit payload, and confirm stays
 * enabled. Length is still capped when provided.
 */
function variantShowsReasonField(variant: ActionConfirmVariant): boolean {
  return variant === 'reject' || variant === 'remove';
}

export function ActionConfirmDialog({
  open,
  onOpenChange,
  variant,
  title,
  description,
  creator,
  confirmLabel,
  reasonMinLength = 0,
  reasonMaxLength = 2000,
  reasonPlaceholder,
  reasonLabel = 'Reason',
  onConfirm,
  busy = false,
  extraContent,
  canConfirm = true,
  confirmText,
  hideConfirmIcon = false,
  onCloseAutoFocus,
}: ActionConfirmDialogProps) {
  const showsReasonField = variantShowsReasonField(variant);
  const reasonRequired = reasonMinLength > 0;
  const [reason, setReason] = useState('');
  const reasonFieldId = useId();

  const requiresTypedConfirm = Boolean(confirmText);
  const [typedConfirm, setTypedConfirm] = useState('');
  const typedConfirmFieldId = useId();
  const armed = !requiresTypedConfirm || isTypedConfirmMatch(typedConfirm, confirmText);

  // Reset reason/typed-confirm whenever the dialog closes so a previous
  // attempt's input doesn't bleed into a fresh action. Tracked via
  // prev-open state (adjust-during-render) instead of an effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setReason('');
      setTypedConfirm('');
    }
  }

  const trimmed = reason.trim();
  // Reason is optional by default (reasonMinLength=0, so an empty value
  // always passes). Callers that pass reasonMinLength > 0 (e.g. a required
  // rejection reason) make emptiness itself invalid.
  const reasonValid =
    (!reasonRequired && trimmed.length === 0) ||
    (trimmed.length >= reasonMinLength && trimmed.length <= reasonMaxLength);

  const handleConfirm = async () => {
    if (busy) return;
    if (!reasonValid) return;
    if (!armed) return;
    // Pass the (possibly empty) trimmed reason for reject/remove; approve
    // ignores it. When the typed-confirm gate is active, forward
    // `{ confirm }` as a second argument; otherwise keep the call arity
    // identical so existing callers are unaffected.
    if (requiresTypedConfirm) {
      await onConfirm(showsReasonField ? trimmed : undefined, { confirm: typedConfirm.trim() });
    } else {
      await onConfirm(showsReasonField ? trimmed : undefined);
    }
  };

  let ConfirmIcon = CONFIRM_BTN_ICON[variant];
  if (hideConfirmIcon) ConfirmIcon = undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      {/* Figma "Modal / Edit profile" shell (nodes 2582:45120 / 2582:45368):
          flat card, no header/footer bands — just a single hairline divider
          ahead of the action row, uniform 26px padding, plain X close. */}
      <DialogContent
        showCloseButton={false}
        onCloseAutoFocus={onCloseAutoFocus}
        className="flex max-w-md flex-col gap-6 overflow-hidden rounded-xl border-none p-[26px] sm:max-w-md"
      >
        {/* Header */}
        <div className="flex w-full items-start gap-[30px]">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-heading-h6/7 font-medium text-foreground">
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="mt-1.5 text-sm/5 text-muted-foreground">
                {description}
              </DialogDescription>
            ) : (
              // Radix complains when DialogContent lacks an associated
              // description / aria-describedby — keep one for screen
              // readers even when the dialog doesn't show body copy.
              <DialogDescription className="sr-only">
                Confirm {DEFAULT_LABELS[variant].toLowerCase()} action.
              </DialogDescription>
            )}
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            aria-label="Close"
            className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        {creator ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
            <div
              className="size-10 shrink-0 rounded-full border border-border bg-secondary bg-cover bg-center"
              style={creator.avatarUrl ? { backgroundImage: `url(${creator.avatarUrl})` } : undefined}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center">
                <span className="truncate text-sm font-semibold text-foreground">{creator.name}</span>
                {creator.isVerified ? (
                  <svg
                    viewBox="0 0 16 16"
                    className="ml-1 inline-block size-3.5 shrink-0 text-blue-600"
                    fill="currentColor"
                    aria-label="Verified"
                  >
                    <circle cx="8" cy="8" r="8" />
                    <path
                      d="M11.5 5.5l-4 4-1.5-1.5"
                      stroke="white"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                ) : null}
              </div>
              {creator.handle ? (
                creator.profileUrl ? (
                  <a
                    href={creator.profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block truncate text-sm text-(--brand-400,#9647f8) hover:text-(--brand-500) hover:underline"
                  >
                    {creator.handle}
                  </a>
                ) : (
                  <div className="truncate text-sm text-(--brand-400,#9647f8)">{creator.handle}</div>
                )
              ) : null}
            </div>
          </div>
        ) : null}

        {creator && (extraContent || showsReasonField || requiresTypedConfirm) ? (
          <div className="h-px w-full shrink-0 bg-border" />
        ) : null}

        {/* Scrollable form region — the tallest extraContent (the
            approve-as-brand deliverable picker) can exceed a short viewport's
            height; scrolling just this region keeps the header, creator card,
            and Cancel/Confirm row always visible instead of pushing Confirm
            off-screen with no way to reach it. */}
        <div className="flex max-h-[50vh] w-full flex-col gap-6 overflow-y-auto">
          {extraContent}

          {showsReasonField ? (
            <div className="flex w-full flex-col gap-1.5">
              <label htmlFor={reasonFieldId} className="text-sm font-medium text-foreground">
                {reasonLabel}
                {reasonRequired ? <span className="text-destructive"> *</span> : null}
              </label>
              <Textarea
                id={reasonFieldId}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonPlaceholder ?? 'Add a reason (optional)'}
                disabled={busy}
                maxLength={reasonMaxLength}
                rows={3}
                className="resize-none"
              />
              <span className="w-full text-right text-sm text-muted-foreground">
                {trimmed.length}/{reasonMaxLength}
              </span>
            </div>
          ) : null}

          {requiresTypedConfirm ? (
            <div className="space-y-1.5">
              {/* select-text overrides Label's own select-none (ui/label.tsx)
                  so the word the user has to retype stays natively
                  click-drag-selectable — matches ShopifyConfirmDeleteDialog
                  and GitHub's own typed-confirm UX (plain selectable text,
                  no dedicated copy button). */}
              {/* gap-0 overrides Label's own flex gap-2 (ui/label.tsx) — see
                  ShopifyConfirmDeleteDialog's identical comment: three
                  sibling children under Label's inherited display:flex each
                  become their own flex item, so the base gap-2 would force
                  an unwanted 8px gap on both sides of the code chip.
                  flex-wrap + the chip's min-w-0 break-all: confirmText here
                  isn't always a short fixed word — apps/brand's team
                  SettingsPage passes a member's email for the remove-member
                  flow, which has no natural break points and would overflow
                  this dialog horizontally without them (a flex item's
                  default min-width is auto, not 0). */}
              <Label htmlFor={typedConfirmFieldId} className="select-text flex-wrap gap-0">
                Write &ldquo;
                <code className="min-w-0 break-all rounded-sm bg-muted px-1 py-0.5 text-xs font-medium">
                  {confirmText}
                </code>
                &rdquo; to confirm
              </Label>
              <Input
                id={typedConfirmFieldId}
                value={typedConfirm}
                autoComplete="off"
                placeholder={confirmText}
                disabled={busy}
                onChange={(e) => setTypedConfirm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && armed && !busy) void handleConfirm();
                }}
              />
              <p className="text-xs text-muted-foreground">This cannot be undone.</p>
            </div>
          ) : null}
        </div>

        {/* Divider ahead of the action row — always present, matching both
            reference frames. */}
        <div className="h-px w-full shrink-0 bg-border" />

        <div className="flex w-full items-center justify-end gap-2.5">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={CONFIRM_BTN_VARIANT[variant]}
            disabled={busy || !reasonValid || !canConfirm || !armed}
            onClick={() => void handleConfirm()}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {confirmLabel ?? DEFAULT_LABELS[variant]}
              </>
            ) : (
              <>
                {confirmLabel ?? DEFAULT_LABELS[variant]}
                {ConfirmIcon ? <ConfirmIcon size={16} weight="bold" /> : null}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ActionConfirmDialog;
